/**
 * NLE export planner — shared by standalone and embedded export paths.
 *
 * `buildNLEExport` walks a project, produces an NLEManifest (list of
 * {kind, sourceKey, target} entries), and builds the FCP7 XML referencing
 * those targets. Audio extension detection goes through an injected
 * AudioBytePeeker so the same planner works for:
 *   - Standalone: peeker reads audioBlobStore (Task 6)
 *   - Embedded:   peeker reads SQLite directly (Task 8)
 *
 * This module does no I/O except through the injected peeker.
 */

import type { TimelineProject, AudioClip } from '@/types/timeline.ts';
import { shotHasVideo, shotHasImage, getShotStartTime } from '@/store/projectDerived.ts';
import { detectAudioFormat } from './audioFormatDetect.ts';
import { buildFCP7XML } from './fcpxmlBuilder.ts';
import type { NLEMediaEntry } from './fcpxmlBuilder.ts';
import type { NLEAudioEntry } from './mediaExtractor.ts';
import { enqueueSendToNative } from '@/bridge/embeddedBridge.ts';

// Set to true to log NLE export planner decisions to Unity console:
//   - Each excluded audio clip with id, label, lane, shotId, and reason
//   - A summary line with kept / excluded counts at the end
const DIAG_NLE_EXPORT = false;

function diagLog(msg: string): void {
  if (!DIAG_NLE_EXPORT) return;
  enqueueSendToNative('diagLog', { msg });
}

// ── Public types ────────────────────────────────────────────────────────

export type NLEExportOptions = {
  fps: number;
  /**
   * Indicates whether the FPS is an NTSC drop-frame rate (23.976, 29.97).
   * NOTE: Currently informational only — buildFCP7XML auto-detects NTSC from
   * the FPS value internally and does not consume this field. Kept on the
   * public type so callers can still surface it in UI / telemetry, and so a
   * future XML builder change can wire it through without an API break.
   */
  ntsc: boolean;
  width: number;
  height: number;
};

/**
 * Injected async function that fetches the first `byteCount` bytes of an
 * audio clip's raw data. Returns null if the clip data is unavailable.
 */
export type AudioBytePeeker = (clipId: number, byteCount: number) => Promise<Uint8Array | null>;

export type NLEManifestEntry =
  | { kind: 'shotImage'; shotId: number; target: string }
  | { kind: 'shotVideo'; shotId: number; target: string }
  | { kind: 'audioClip'; clipId: number; target: string };

export type NLEManifest = {
  version: 1;
  zipBaseName: string;
  xmlFilename: string;
  media: NLEManifestEntry[];
};

export type NLEExportSummary = {
  shotCount: number;
  audioClipCount: number;
  excluded: {
    shotsWithoutMedia: number;
    audioWithoutShot: number;
    zeroDurationShots: number;
    zeroDurationAudio: number;
  };
};

// ── Internal constants ──────────────────────────────────────────────────

const LANE_PREFIXES = ['dialog', 'music', 'sfx', 'vo'] as const;

/** Characters that are illegal in filenames on Windows, macOS, and Linux */
const FILENAME_BAD_CHARS = /[<>:"/\\|?*]/g;

// ── Helpers ────────────────────────────────────────────────────────────

function sanitize(s: string): string {
  return s.replace(FILENAME_BAD_CHARS, '_');
}

function sanitizeProjectName(name: string): string {
  const cleaned = (name ?? '').replace(FILENAME_BAD_CHARS, '_').trim();
  return cleaned.length > 0 ? cleaned : 'export';
}

// ── Main export ────────────────────────────────────────────────────────

export async function buildNLEExport(
  project: TimelineProject,
  audioClips: AudioClip[],
  options: NLEExportOptions,
  audioPeeker: AudioBytePeeker,
): Promise<{ manifest: NLEManifest; xmlString: string; summary: NLEExportSummary }> {
  // Wiring strategy: Option A — build the rich NLEMediaEntry / NLEAudioEntry
  // arrays that buildFCP7XML already expects, derived from project data and the
  // same per-shot/per-clip information already available in this planner. This
  // keeps fcpxmlBuilder.ts completely unchanged (preserving byte-for-byte XML
  // output for the Phase 1-3 standalone path) and localises all adaptation here.
  const media: NLEManifestEntry[] = [];
  const summary: NLEExportSummary = {
    shotCount: 0,
    audioClipCount: 0,
    excluded: {
      shotsWithoutMedia: 0,
      audioWithoutShot: 0,
      zeroDurationShots: 0,
      zeroDurationAudio: 0,
    },
  };

  // Build a set of all valid shot IDs in this project for O(1) orphan-clip detection.
  // NOTE: We deliberately do NOT use `getShotStartTime(project, clip.shotId) < 0`
  // (which earlier prose specs proposed) — `getShotStartTime` returns 0 for missing
  // shot IDs, never a negative value, so the `< 0` guard would be a no-op and
  // orphan clips would slip through. A Set membership check is the correct fix.
  const validShotIds = new Set<number>();
  for (const scene of project.scenes) {
    for (const shot of scene.shots) {
      validShotIds.add(shot.id);
    }
  }

  // Rich maps for fcpxmlBuilder — built in parallel with the manifest entries.
  const shotMediaMap = new Map<number, NLEMediaEntry>();
  const audioEntriesList: NLEAudioEntry[] = [];

  // ── Pass 1: shots ──────────────────────────────────────────────────

  for (const scene of project.scenes) {
    if (scene.isOmitted) continue;
    for (const shot of scene.shots) {
      if (!shot.duration || shot.duration <= 0) {
        summary.excluded.zeroDurationShots += 1;
        continue;
      }

      const sceneNum = sanitize(String(scene.number));
      const shotNum = sanitize(String(shot.number));
      // Append shot.id as a disambiguator so DaVinci Resolve doesn't auto-detect
      // numerically adjacent files (e.g. 1_A.jpg + 2_A.jpg from two single-shot
      // scenes) as an image sequence and merge them into one media-pool entry.
      // The shot id is unique per project and non-sequential, which breaks the
      // "looks like frame 1, frame 2" heuristic. Premiere Pro is unaffected.
      const label = `${sceneNum}_${shotNum}_${shot.id}`;

      if (shotHasVideo(shot)) {
        const target = `video/${label}.mp4`;
        media.push({ kind: 'shotVideo', shotId: shot.id, target });
        shotMediaMap.set(shot.id, {
          path: target,
          mime: 'video/mp4',
          duration: shot.duration,
          width: options.width,
          height: options.height,
        });
        summary.shotCount += 1;
      } else if (shotHasImage(shot)) {
        const target = `video/${label}.jpg`;
        media.push({ kind: 'shotImage', shotId: shot.id, target });
        shotMediaMap.set(shot.id, {
          path: target,
          mime: 'image/jpeg',
          duration: shot.duration,
          width: options.width,
          height: options.height,
        });
        summary.shotCount += 1;
      } else {
        summary.excluded.shotsWithoutMedia += 1;
      }
    }
  }

  // ── Pass 2: audio clips ────────────────────────────────────────────

  for (const clip of audioClips) {
    if (!clip.duration || clip.duration <= 0) {
      summary.excluded.zeroDurationAudio += 1;
      diagLog(`[NLE Export] EXCLUDED audio clip ${clip.id} ("${clip.label}", lane ${clip.lane}, shotId ${clip.shotId}): duration is ${clip.duration}`);
      continue;
    }

    if (!validShotIds.has(clip.shotId)) {
      summary.excluded.audioWithoutShot += 1;
      diagLog(`[NLE Export] EXCLUDED audio clip ${clip.id} ("${clip.label}", lane ${clip.lane}): shotId ${clip.shotId} is not in any scene (orphan reference)`);
      continue;
    }

    const headerBytes = (await audioPeeker(clip.id, 512)) ?? new Uint8Array(0);
    const fmt = detectAudioFormat(headerBytes, clip.mimeType);
    const lane = clip.lane ?? 0;
    const prefix = LANE_PREFIXES[lane] ?? 'audio';
    const target = `audio/${prefix}_${clip.id}.${fmt.ext}`;
    media.push({ kind: 'audioClip', clipId: clip.id, target });
    summary.audioClipCount += 1;

    // Build rich NLEAudioEntry for the XML generator.
    // absoluteStart matches extractAllAudio's computation:
    //   getShotStartTime(project, clip.shotId) + clip.shotOffset
    const absoluteStart = getShotStartTime(project, clip.shotId) + clip.shotOffset;
    const mime = fmt.ext === 'mp3' ? 'audio/mpeg' : fmt.ext === 'ogg' ? 'audio/ogg' : 'audio/wav';
    audioEntriesList.push({
      path: target,
      mime,
      channels: fmt.channels,
      duration: clip.duration,
      clipId: clip.id,
      absoluteStart,
      volume: clip.volume,
      isMuted: clip.isMuted,
      lane,
      bufferOffset: clip.bufferOffset,
      fadeInTime: clip.fadeInTime,
      fadeOutTime: clip.fadeOutTime,
    });
  }

  // ── XML ────────────────────────────────────────────────────────────

  const xmlString = buildFCP7XML({
    project,
    fps: options.fps,
    width: options.width,
    height: options.height,
    shotMedia: shotMediaMap,
    audioEntries: audioEntriesList,
  });

  // ── Manifest ───────────────────────────────────────────────────────

  const manifest: NLEManifest = {
    version: 1,
    zipBaseName: `${sanitizeProjectName(project.title)}_NLE`,
    xmlFilename: 'timeline.xml',
    media,
  };

  diagLog(
    `[NLE Export] Summary: ${summary.shotCount} shots + ${summary.audioClipCount} audio clips kept; ` +
    `excluded: ${summary.excluded.zeroDurationShots} zero-dur shots, ` +
    `${summary.excluded.shotsWithoutMedia} shots without media, ` +
    `${summary.excluded.zeroDurationAudio} zero-dur audio, ` +
    `${summary.excluded.audioWithoutShot} orphan audio (input: ${audioClips.length} audio clips total)`
  );

  return { manifest, xmlString, summary };
}
